/**
 * @module composables/useActivityFeedFetch
 * @description 실시간 활동 피드 — 최신 10건 fetch + 3분 폴링.
 * (SURINP-208 — SURINP-183 ADDENDUM에서 페이지 넘김 제거 후 단순화)
 *
 * 정책:
 * - page=1, size=10 고정 호출. 페이지네이션 없음.
 * - 3분(180,000ms) 폴링 + 탭 비활성(`document.hidden`) 시 스킵.
 * - 첫 fetch만 loading=true (이후 폴링은 깜빡임 방지).
 * - 응답 실패 시 items=[] + window.__APP_LOG__ 호출.
 * - 응답에 size 초과 항목이 와도 클라이언트에서 방어적 슬라이스.
 */
import { ref, onMounted, onUnmounted } from 'vue'
import { getActivityFeed } from '@/api/domain/dashboard'

/** 폴링 주기 (밀리초) — 3분 */
const POLL_INTERVAL_MS = 180_000

/** 백엔드에 요청할 페이지 번호 (1-based 고정) */
const PAGE = 1

/** 백엔드에 요청할 페이지 크기 (10건 고정) */
const SIZE = 10

/**
 * ActivityFeed 단순 fetch + 3분 폴링을 관리한다.
 *
 * 반환값:
 * - items: 현재 fetch된 아이템 배열 (최대 SIZE 건)
 * - loading: 첫 fetch 진행 중 여부 (이후 폴링은 false 유지)
 * - fetchFeed(): 강제 새로고침
 *
 * @returns {{
 *   items: import('vue').Ref<Array<object>>,
 *   loading: import('vue').Ref<boolean>,
 *   fetchFeed: () => Promise<void>
 * }}
 */
export function useActivityFeedFetch() {
  // ── 상태 (State) ──

  const items = ref([])
  const loading = ref(false)

  /** setInterval 타이머 ID */
  let timerId = null

  /** 최초 fetch 완료 여부. true 이후로는 loading을 활성화하지 않아 폴링 시 깜빡임 방지. */
  let hasFetchedOnce = false

  // ── 메서드 (Methods) ──

  /**
   * 활동 피드를 fetch한다. 응답이 없거나 실패하면 items를 비운다(첫 호출 한정).
   * 폴링 호출에서는 기존 items를 유지하여 갑작스러운 화면 공백을 방지한다.
   *
   * @returns {Promise<void>}
   */
  async function fetchFeed() {
    if (loading.value) return
    const isFirstFetch = !hasFetchedOnce
    if (isFirstFetch) loading.value = true
    try {
      const res = await getActivityFeed(PAGE, SIZE)
      if (res && Array.isArray(res.items)) {
        // 백엔드가 size 무시하고 더 보내도 SIZE까지만 표시 (방어적)
        items.value = res.items.slice(0, SIZE)
      } else if (isFirstFetch) {
        items.value = []
      }
    } catch (e) {
      if (typeof window !== 'undefined' && typeof window.__APP_LOG__ === 'function') {
        window.__APP_LOG__(e)
      }
      if (isFirstFetch) items.value = []
    } finally {
      if (isFirstFetch) loading.value = false
      hasFetchedOnce = true
    }
  }

  /**
   * 3분 폴링 시작. 탭이 비활성화(`document.hidden===true`)되어 있으면 fetch 스킵.
   *
   * @returns {void}
   */
  function startPolling() {
    if (timerId !== null) return
    timerId = setInterval(() => {
      if (typeof document !== 'undefined' && !document.hidden) {
        fetchFeed()
      }
    }, POLL_INTERVAL_MS)
  }

  /**
   * 폴링 중지. 컴포넌트 언마운트 시 호출.
   *
   * @returns {void}
   */
  function stopPolling() {
    if (timerId !== null) {
      clearInterval(timerId)
      timerId = null
    }
  }

  // ── 라이프사이클 (Lifecycle) ──

  onMounted(() => {
    fetchFeed()
    startPolling()
  })

  onUnmounted(stopPolling)

  return {
    items,
    loading,
    fetchFeed
  }
}
